import {
  cloneElement,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
  type CSSProperties,
} from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'

export function StatusBadge({
  color,
  children,
}: {
  color?: string
  children: ReactNode
}) {
  const palette: Record<string, string> = {
    green: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    volcano: 'bg-red-50 text-red-700',
    gold: 'bg-amber-50 text-amber-800',
    orange: 'bg-orange-50 text-orange-700',
    blue: 'bg-blue-50 text-blue-700',
    geekblue: 'bg-indigo-50 text-indigo-700',
    purple: 'bg-purple-50 text-purple-700',
  }
  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${palette[color || ''] || 'bg-muted text-muted-foreground'}`}
    >
      {children}
    </span>
  )
}
export function Notice({
  tone,
  title,
  description,
  action,
  style,
}: {
  tone?: string
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  style?: CSSProperties
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      style={style}
      className={`rounded-xl border p-4 ${tone === 'error' ? 'border-red-200 bg-red-50' : tone === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-violet-100 bg-violet-50/50'}`}
    >
      <div className="font-medium">{title}</div>
      {description && (
        <div className="mt-1 text-sm text-muted-foreground">{description}</div>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}
export function CopyText({
  text,
  children,
  ...props
}: {
  text?: string
  children: ReactNode
  style?: CSSProperties
}) {
  return (
    <span {...props} className="inline-flex items-center gap-1 break-all">
      {children}
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label="複製"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text ?? String(children))
            toast.success('已複製到剪貼板')
          } catch {
            toast.error('複製失敗')
          }
        }}
      >
        <Copy />
      </Button>
    </span>
  )
}
export function ConfirmAction({
  title,
  description,
  onConfirm,
  children,
}: {
  title: ReactNode
  description?: ReactNode
  onConfirm: () => unknown
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  return (
    <>
      {cloneElement(children as ReactElement<{ onClick: () => void }>, {
        onClick: () => setOpen(true),
      })}
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={title}
        footer={
          <>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => setOpen(false)}
            >
              取消
            </Button>
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  await onConfirm()
                  setOpen(false)
                } finally {
                  setBusy(false)
                }
              }}
            >
              確認
            </Button>
          </>
        }
      >
        {description}
      </Modal>
    </>
  )
}
export function DateRangeField({
  value,
  onChange,
  placeholder = ['開始日期', '結束日期'],
}: {
  value: [Dayjs | null, Dayjs | null]
  onChange: (value: [Dayjs | null, Dayjs | null]) => void
  placeholder?: [string, string]
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <div className="flex max-w-full items-center gap-2">
      {([0, 1] as const).map((index) => (
        <Input
          key={index}
          type="date"
          aria-label={placeholder[index]}
          value={draft[index]?.format('YYYY-MM-DD') || ''}
          onChange={(event) => {
            const next: [Dayjs | null, Dayjs | null] = [...draft]
            next[index] = event.target.value ? dayjs(event.target.value) : null
            if (next[0] && next[1] && next[0].isAfter(next[1])) {
              next.reverse()
            }
            setDraft(next)
            if (next[0] && next[1]) onChange(next)
            else if (!event.target.value) onChange([null, null])
          }}
        />
      ))}
    </div>
  )
}
export function SearchField({
  onSearch,
  ...props
}: React.ComponentProps<typeof Input> & { onSearch: (value: string) => void }) {
  return (
    <div className="flex items-center gap-1">
      <Input
        {...props}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSearch(event.currentTarget.value)
        }}
      />
      <Button
        variant="outline"
        onClick={() => onSearch(String(props.value || ''))}
      >
        查詢
      </Button>
    </div>
  )
}
